import React from 'react';
import { Card, CardContent, Tooltip, IconButton, Box, Typography } from '@mui/material';
import { InfoOutlined, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { neutral, success, warning, error } from '_lex/_foundations/phxColors';
import numeral from 'numeral';

type MetricFormat = 'number' | 'currency' | 'percent';
type ChartInfoColor = 'success' | 'warning' | 'error' | 'neutral';
type ChartInfoTrend = 'increasing' | 'neutral' | 'decreasing';

interface ChartInfoProps {
  /** The title/label for the metric */
  title: string;
  /** The main metric value to display */
  metric: number | React.ReactNode;
  /** Optional description text shown in the bottom right */
  description?: string;
  /** Optional tooltip text - when provided, shows info icon */
  tooltip?: string;
  /** Format for numeric metrics - defaults to 'number' */
  metricFormat?: MetricFormat;
  /** Color theme for border and description text - defaults to 'neutral' */
  color?: ChartInfoColor;
  /** Trend indicator - when provided, replaces description with trend icon and text */
  trend?: ChartInfoTrend;
}

/**
 * ChartInfo component displays a metric card with title, value, description, and optional tooltip.
 * Based on the lex-metric-card theme style.
 */
export const ChartInfo: React.FC<ChartInfoProps> = ({
  title,
  metric,
  description,
  tooltip,
  metricFormat = 'number',
  color = 'neutral',
  trend,
}) => {
  const getColorValues = (color: ChartInfoColor) => {
    switch (color) {
      case 'success':
        return {
          border: success[500],
          description: neutral[500], // Badge default text color
        };
      case 'warning':
        return {
          border: warning[500],
          description: neutral[500], // Badge default text color
        };
      case 'error':
        return {
          border: error[800],
          description: neutral[500], // Badge default text color
        };
      case 'neutral':
      default:
        return {
          border: neutral[300],
          description: neutral[500],
        };
    }
  };

  const colorValues = getColorValues(color);

  const getTrendInfo = (trend: ChartInfoTrend) => {
    switch (trend) {
      case 'increasing':
        return {
          text: 'Rising',
          icon: TrendingUp,
        };
      case 'decreasing':
        return {
          text: 'Falling',
          icon: TrendingDown,
        };
      case 'neutral':
      default:
        return {
          text: 'Stable',
          icon: TrendingFlat,
        };
    }
  };

  const formatMetric = (value: number | React.ReactNode, format: MetricFormat): string | React.ReactNode => {
    if (typeof value !== 'number') {
      return value;
    }

    const hasDecimals = value % 1 !== 0;
    const decimalFormat = hasDecimals ? '.00' : '';

    switch (format) {
      case 'currency':
        return numeral(value).format(`$0,0${decimalFormat}`);
      case 'percent':
        return numeral(value).format(`0${decimalFormat}%`);
      case 'number':
      default:
        return numeral(value).format(`0,0${decimalFormat}`);
    }
  };

  const formattedMetric = formatMetric(metric, metricFormat);

  const cardContent = (
    <Card
      sx={{
        backgroundColor: neutral[25],
        borderRadius: 2,
        border: `solid 1px ${colorValues.border}`,
        gap: 2,
        padding: 4,
        transition: 'none', // Disable MUI's default transitions
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {/* Header with title and optional info icon */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 3,
              alignItems: 'center',
              pr: tooltip ? 1 : 0,
            }}
          >
            <Typography
              variant='subtitleMd'
              sx={{
                color: neutral[500],
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {title}
            </Typography>
            {tooltip && (
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': {
                    fontSize: 16,
                    color: '#5220B5', // Badge-info-icon color from Figma
                  },
                }}
              >
                <InfoOutlined />
              </Box>
            )}
          </Box>

          {/* Bottom row with metric and description */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            {/* Metric value */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 0.5,
              }}
            >
              <Typography
                variant='displaySm'
                sx={{
                  color: neutral[800],
                  fontWeight: 500,
                  lineHeight: 1.15,
                  fontSize: '32px',
                }}
              >
                {formattedMetric}
              </Typography>
            </Box>

            {/* Description badge or Trend indicator */}
            {(description || trend) && (
              <Box
                sx={{
                  height: 22,
                  px: 1,
                  py: 0.25,
                  borderRadius: '100px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  gap: 0.5,
                }}
              >
                {trend && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': {
                        fontSize: 12,
                        color: neutral[500], // Badge default icon color
                      },
                    }}
                  >
                    {React.createElement(getTrendInfo(trend).icon)}
                  </Box>
                )}
                <Typography
                  sx={{
                    color: colorValues.description,
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: 1.2,
                  }}
                >
                  {trend ? getTrendInfo(trend).text : description}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Wrap with tooltip if tooltip is provided
  if (tooltip) {
    return (
      <Tooltip
        title={tooltip}
        arrow
      >
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

export default ChartInfo;
